import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip
} from '@material-ui/core';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useVehicle } from '../../contexts/VehicleContext';
import { useTrip } from '../../contexts/TripContext';
import { useCompany } from '../../contexts/CompanyContext';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Status color mapping
const STATUS_COLORS = {
  active: 'success',
  inactive: 'error',
  maintenance: 'warning',
  reserved: 'primary'
};

const VehiclesDashboard = () => {
  const { getVehicles, getVehicleStats, getVehicleMaintenanceHistory } = useVehicle();
  const { getTrips } = useTrip();
  const { getCompanies } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [companyFilter, setCompanyFilter] = useState('all');
  
  // Dashboard data
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    inactiveVehicles: 0,
    maintenanceVehicles: 0,
    averageUtilization: 0,
    totalCapacity: 0
  });
  
  // Chart data
  const [vehiclesByStatusData, setVehiclesByStatusData] = useState([]);
  const [vehiclesByTypeData, setVehiclesByTypeData] = useState([]);
  const [vehicleUtilizationData, setVehicleUtilizationData] = useState([]);
  const [maintenanceFrequencyData, setMaintenanceFrequencyData] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchVehiclesDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all required data
        const allVehicles = await getVehicles();
        const allTrips = await getTrips();
        const allCompanies = await getCompanies();
        
        setCompanies(allCompanies);
        setTrips(allTrips);
        
        // Filter vehicles based on company filter
        let filteredVehicles = allVehicles;
        if (companyFilter !== 'all') {
          filteredVehicles = allVehicles.filter(vehicle => vehicle.companyId === companyFilter);
        }
        
        setVehicles(filteredVehicles);
        
        // Calculate basic stats
        const totalVehicles = filteredVehicles.length;
        const activeVehicles = filteredVehicles.filter(vehicle => vehicle.status === 'active').length;
        const inactiveVehicles = filteredVehicles.filter(vehicle => vehicle.status === 'inactive').length;
        const maintenanceVehicles = filteredVehicles.filter(vehicle => vehicle.status === 'maintenance').length;
        
        // Calculate total capacity
        const totalCapacity = filteredVehicles.reduce((total, vehicle) => total + (vehicle.capacity || 0), 0);
        
        // Calculate vehicle utilization
        const now = new Date();
        let startDate;
        
        switch (timeRange) {
          case 'week':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            break;
          default:
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
        }
        
        // Filter trips in the time range
        const tripsInRange = allTrips.filter(trip => {
          const tripDate = new Date(trip.departureTime.seconds * 1000);
          return tripDate >= startDate && tripDate <= now;
        });
        
        // Calculate utilization for each vehicle
        const vehicleUtilization = {};
        const vehicleTripsCount = {};
        
        filteredVehicles.forEach(vehicle => {
          vehicleUtilization[vehicle.id] = 0;
          vehicleTripsCount[vehicle.id] = 0;
        });
        
        tripsInRange.forEach(trip => {
          if (vehicleUtilization[trip.vehicleId] !== undefined) {
            vehicleUtilization[trip.vehicleId]++;
            vehicleTripsCount[trip.vehicleId]++;
          }
        });
        
        // Calculate average utilization
        let totalUtilization = 0;
        let vehiclesWithTrips = 0;
        
        Object.keys(vehicleUtilization).forEach(vehicleId => {
          if (vehicleTripsCount[vehicleId] > 0) {
            totalUtilization += vehicleUtilization[vehicleId];
            vehiclesWithTrips++;
          }
        });
        
        const averageUtilization = vehiclesWithTrips > 0 ? totalUtilization / vehiclesWithTrips : 0;
        
        setStats({
          totalVehicles,
          activeVehicles,
          inactiveVehicles,
          maintenanceVehicles,
          averageUtilization,
          totalCapacity
        });
        
        // Prepare vehicles by status data for chart
        const vehiclesByStatus = [
          { name: 'Active', value: activeVehicles },
          { name: 'Inactive', value: inactiveVehicles },
          { name: 'Maintenance', value: maintenanceVehicles },
          { name: 'Reserved', value: filteredVehicles.filter(vehicle => vehicle.status === 'reserved').length }
        ];
        setVehiclesByStatusData(vehiclesByStatus);
        
        // Prepare vehicles by type data
        const typeCounts = {};
        
        filteredVehicles.forEach(vehicle => {
          const type = vehicle.type || 'Unknown';
          if (!typeCounts[type]) {
            typeCounts[type] = 0;
          }
          typeCounts[type]++;
        });
        
        const vehiclesByType = Object.keys(typeCounts).map(type => ({
          name: type,
          value: typeCounts[type]
        }));
        
        setVehiclesByTypeData(vehiclesByType);
        
        // Prepare vehicle utilization data
        const utilizationData = filteredVehicles
          .filter(vehicle => vehicleTripsCount[vehicle.id] > 0)
          .map(vehicle => ({
            name: vehicle.registrationNumber || vehicle.id.substring(0, 8),
            value: vehicleTripsCount[vehicle.id]
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Top 10 vehicles by utilization
        
        setVehicleUtilizationData(utilizationData);
        
        // Prepare maintenance frequency data
        const maintenanceByMonth = {};
        
        // Initialize all months in the last year
        for (let i = 0; i < 12; i++) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
          maintenanceByMonth[monthKey] = 0;
        }
        
        // Count maintenance events by month
        for (const vehicle of filteredVehicles) {
          if (vehicle.maintenanceHistory) {
            vehicle.maintenanceHistory.forEach(event => {
              const eventDate = new Date(event.date.seconds * 1000);
              const monthKey = eventDate.toLocaleDateString('en-US', { month: 'short' });
              if (maintenanceByMonth[monthKey] !== undefined) {
                maintenanceByMonth[monthKey]++;
              }
            });
          }
        }
        
        // Convert to array format for chart
        const maintenanceFrequency = Object.keys(maintenanceByMonth)
          .map(month => ({
            month,
            count: maintenanceByMonth[month]
          }))
          .reverse(); // Show oldest to newest
        
        setMaintenanceFrequencyData(maintenanceFrequency);
        
      } catch (error) {
        console.error('Error fetching vehicles dashboard data:', error);
        setError('Failed to load vehicles dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVehiclesDashboardData();
  }, [timeRange, companyFilter]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleCompanyFilterChange = (event) => {
    setCompanyFilter(event.target.value);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Helper function to get company name
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Vehicles Dashboard
      </Typography>
      
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Time Range"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyFilter}
            onChange={handleCompanyFilterChange}
            label="Company"
          >
            <MenuItem value="all">All Companies</MenuItem>
            {companies.map(company => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Vehicles
              </Typography>
              <Typography variant="h4">
                {stats.totalVehicles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active
              </Typography>
              <Typography variant="h4">
                {stats.activeVehicles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Inactive
              </Typography>
              <Typography variant="h4">
                {stats.inactiveVehicles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Maintenance
              </Typography>
              <Typography variant="h4">
                {stats.maintenanceVehicles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg. Utilization
              </Typography>
              <Typography variant="h4">
                {stats.averageUtilization.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Capacity
              </Typography>
              <Typography variant="h4">
                {stats.totalCapacity}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        {/* Vehicles by Status Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Vehicles by Status
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={vehiclesByStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vehiclesByStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Vehicles by Type Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Vehicles by Type
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={vehiclesByTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vehiclesByTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Vehicle Utilization Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Vehicles by Utilization
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={vehicleUtilizationData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Trips" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Maintenance Frequency Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Maintenance Frequency
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={maintenanceFrequencyData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#FF8042" 
                  activeDot={{ r: 8 }} 
                  name="Maintenance Events"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Vehicles Table */}
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Vehicle Fleet
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Registration</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Maintenance</TableCell>
                <TableCell>Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((vehicle) => {
                  // Get last maintenance date
                  const lastMaintenance = vehicle.maintenanceHistory && vehicle.maintenanceHistory.length > 0
                    ? new Date(vehicle.maintenanceHistory[0].date.seconds * 1000).toLocaleDateString()
                    : 'N/A';
                  
                  return (
                    <TableRow key={vehicle.id}>
                      <TableCell>{vehicle.registrationNumber || 'N/A'}</TableCell>
                      <TableCell>{vehicle.type || 'N/A'}</TableCell>
                      <TableCell>{vehicle.capacity || 'N/A'}</TableCell>
                      <TableCell>{getCompanyName(vehicle.companyId)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={vehicle.status} 
                          color={STATUS_COLORS[vehicle.status] || 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{lastMaintenance}</TableCell>
                      <TableCell>{vehicle.utilization || 0} trips</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={vehicles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default VehiclesDashboard;
